import React, { useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import Layout from "@/components/Layout";
import { GenieCard } from "@/components/GenieLauncher";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import MediaSlider from "@/components/MediaSlider";
import { SocialGate } from "@/components/SocialCTA";
import {
  Users, Loader2, Rocket, MapPin, ExternalLink, BadgeCheck, MessageCircle,
  Check, X, PackagePlus, ImagePlus, Instagram,
  Music, Disc3, Camera, Video, Building2, Palette, Megaphone, Package,
  UserSquare, Handshake, Sparkles, Speaker, Wrench,
} from "lucide-react";

const EVENTOM_WA = "917250120666";

/**
 * Ecosystem categories — one source of truth for BOTH the listing form
 * and the public directory tabs. Order roughly mirrors funnel importance
 * (performers first, then support crew, then commercial partners).
 * `key` MUST match the `type` stored in `db.ecosystem_leads`.
 *
 * NOTE on colours: Tailwind purges class strings that don't literally appear
 * in the source, so per-accent classes must be full static strings — no
 * `bg-${accent}/15` interpolation.
 */
const ACCENT_STYLES = {
  magenta: {
    tileHover: "hover:border-magenta/60 hover:bg-magenta/5",
    iconBg: "bg-magenta/15 text-magenta border-magenta/30",
    activeBorder: "border-magenta bg-magenta/15",
    activeIcon: "text-magenta",
  },
  skyx: {
    tileHover: "hover:border-skyx/60 hover:bg-skyx/5",
    iconBg: "bg-skyx/15 text-skyx border-skyx/30",
    activeBorder: "border-skyx bg-skyx/15",
    activeIcon: "text-skyx",
  },
  lime: {
    tileHover: "hover:border-lime/60 hover:bg-lime/5",
    iconBg: "bg-lime/15 text-lime border-lime/30",
    activeBorder: "border-lime bg-lime/15",
    activeIcon: "text-lime",
  },
  pink: {
    tileHover: "hover:border-pink/60 hover:bg-pink/5",
    iconBg: "bg-pink/15 text-pink border-pink/30",
    activeBorder: "border-pink bg-pink/15",
    activeIcon: "text-pink",
  },
  orangex: {
    tileHover: "hover:border-orangex/60 hover:bg-orangex/5",
    iconBg: "bg-orangex/15 text-orangex border-orangex/30",
    activeBorder: "border-orangex bg-orangex/15",
    activeIcon: "text-orangex",
  },
};

const CATEGORIES = [
  { key: "artist",       label: "Artist / Band",         icon: Music,      accent: "magenta" },
  { key: "dj",           label: "DJ",                     icon: Disc3,      accent: "skyx" },
  { key: "creator",      label: "Creator / Influencer",   icon: Sparkles,   accent: "lime" },
  { key: "photographer", label: "Photographer",           icon: Camera,     accent: "pink" },
  { key: "videographer", label: "Videographer",           icon: Video,      accent: "skyx" },
  { key: "production",   label: "Event Production",       icon: Wrench,     accent: "orangex" },
  { key: "avsound",      label: "AV & Sound",             icon: Speaker,    accent: "magenta" },
  { key: "decor",        label: "Decor & Design",         icon: Palette,    accent: "pink" },
  { key: "venue",        label: "Venue",                  icon: Building2,  accent: "lime" },
  { key: "marketing",    label: "Marketing Agency",       icon: Megaphone,  accent: "orangex" },
  { key: "agency",       label: "Talent / Event Agency",  icon: Users,      accent: "skyx" },
  { key: "vendor",       label: "Vendor (Catering / F&B / Security / Mehendi…)", icon: Package, accent: "lime" },
  { key: "freelancer",   label: "Freelancer",             icon: UserSquare, accent: "magenta" },
  { key: "sponsor",      label: "Sponsor / Brand",        icon: Handshake,  accent: "orangex" },
];

/**
 * Directory groups — 4 top-level categories as per Beta brief:
 * Sponsors · Artists · Vendors · Freelancers.
 * Each category rolls up multiple underlying `type` keys so the DB
 * schema stays the same but the user sees a clean 4-way filter.
 */
const DIRECTORY_GROUPS = [
  { key: "sponsors",    label: "Sponsors",    types: ["sponsor", "agency", "marketing"] },
  { key: "artists",     label: "Artists",     types: ["artist", "dj", "creator"] },
  { key: "vendors",     label: "Vendors",     types: ["vendor", "venue", "avsound", "decor", "production"] },
  { key: "freelancers", label: "Freelancers", types: ["freelancer", "photographer", "videographer"] },
  { key: "exclusive",   label: "Eventom Exclusive", types: [], exclusiveFlag: true },
];

// Legacy shape used by the listing form (label lookup) — kept for compatibility.
const TYPES = CATEGORIES.map((c) => [c.key, c.label]);
const TYPE_LABEL = Object.fromEntries(TYPES);
const TILE = ["tile", "tile tile-cyan", "tile tile-magenta", "tile tile-lime"];

function ComboBar({ selected, partners, clear }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ organiser_name: "", contact_mobile: "", contact_email: "", city: "", event_brief: "" });
  const chosen = partners.filter((p) => selected.includes(p.id));
  if (chosen.length === 0) return null;

  const summary = chosen.map((p) => `${p.name} (${TYPE_LABEL[p.type] || p.type}, ${p.city})`).join(", ");

  const submit = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      await api.post("/ecosystem/combo-enquiry", { ...form, partner_ids: chosen.map((p) => p.id), partner_summary: summary });
      const text = `Hi Eventom! I'd like a combo package for my event${form.city ? ` in ${form.city}` : ""}.\n\nPartners: ${summary}\n\n${form.event_brief || ""}\n\n— ${form.organiser_name} (${form.contact_mobile})`;
      window.open(`https://wa.me/${EVENTOM_WA}?text=${encodeURIComponent(text)}`, "_blank", "noopener");
      toast.success("Combo enquiry raised — our team will confirm on WhatsApp");
      setOpen(false); clear();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Could not send enquiry"); }
    finally { setBusy(false); }
  };

  return (
    <>
      <div className="fixed bottom-24 md:bottom-6 left-1/2 -translate-x-1/2 z-[65] w-[calc(100%-2rem)] max-w-lg" data-testid="combo-bar">
        <div className="genie-glow rounded-full flex items-center gap-3 pl-5 pr-2 py-2 bg-surface/95 backdrop-blur-xl">
          <div className="flex-1 min-w-0">
            <div className="font-heading font-bold text-sm">{chosen.length} selected</div>
            <div className="text-[10px] font-mono uppercase tracking-widest text-muted truncate">{chosen.map((p) => p.name).join(" · ")}</div>
          </div>
          <button onClick={clear} data-testid="combo-clear" className="icon-circle !w-9 !h-9 !rounded-full"><X className="w-4 h-4" /></button>
          <button onClick={() => setOpen(true)} data-testid="combo-open" className="btn-lime !py-2.5 !px-4 !text-xs shrink-0"><PackagePlus className="w-4 h-4" /> Combo enquiry</button>
        </div>
      </div>
      {open && (
        <div className="fixed inset-0 z-[80] bg-black/75 backdrop-blur-md flex items-end sm:items-center justify-center p-4" onClick={() => setOpen(false)}>
          <form onSubmit={submit} onClick={(e) => e.stopPropagation()} className="glass w-full max-w-md p-6 space-y-3" data-testid="combo-form">
            <div className="font-display text-3xl">One enquiry. <span className="grad-text">Whole crew.</span></div>
            <p className="text-xs text-muted">We negotiate a combo package for {chosen.length} partner{chosen.length > 1 ? "s" : ""} and reply on WhatsApp.</p>
            <div className="flex flex-wrap gap-1.5">
              {chosen.map((p) => <span key={p.id} className="chip !text-[9px]">{p.name}</span>)}
            </div>
            <input required data-testid="combo-name" placeholder="Your name / organisation" value={form.organiser_name} onChange={(e) => setForm({ ...form, organiser_name: e.target.value })} className="input-dark" />
            <div className="grid grid-cols-2 gap-3">
              <input required data-testid="combo-mobile" placeholder="Mobile" value={form.contact_mobile} onChange={(e) => setForm({ ...form, contact_mobile: e.target.value })} className="input-dark" />
              <input data-testid="combo-city" placeholder="Event city" value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} className="input-dark" />
            </div>
            <input type="email" data-testid="combo-email" placeholder="Email (optional)" value={form.contact_email} onChange={(e) => setForm({ ...form, contact_email: e.target.value })} className="input-dark" />
            <textarea rows={2} data-testid="combo-brief" placeholder="Event date, scale, budget range…" value={form.event_brief} onChange={(e) => setForm({ ...form, event_brief: e.target.value })} className="input-dark resize-none" />
            <button disabled={busy} className="btn-primary w-full justify-center" data-testid="combo-submit">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <MessageCircle className="w-4 h-4" />} Send & open WhatsApp
            </button>
          </form>
        </div>
      )}
    </>
  );
}

function PartnerDirectory() {
  const [partners, setPartners] = useState([]);
  const [brandSponsors, setBrandSponsors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [params, setParams] = useSearchParams();
  const [selected, setSelected] = useState([]);
  const [cityFilter, setCityFilter] = useState("");
  const genreParam = params.get("genre") || "";

  useEffect(() => {
    api.get("/ecosystem/partners").then(({ data }) => setPartners(data || [])).catch(() => {}).finally(() => setLoading(false));
    api.get("/brands/partners").then(({ data }) => setBrandSponsors(data || [])).catch(() => {});
  }, []);

  // Build the visible groups from the master directory list — hide empty ones
  // (except "Eventom Exclusive" which always shows because it's a curated slot).
  const groups = useMemo(() => {
    return DIRECTORY_GROUPS.filter((g) => {
      if (g.exclusiveFlag) return true;
      if (g.key === "sponsors" && brandSponsors.length > 0) return true;
      return g.types.some((t) => partners.some((p) => p.type === t));
    });
  }, [partners, brandSponsors]);

  const active = groups.some((g) => g.key === genreParam) ? genreParam : (groups[0]?.key || "");
  const activeGroup = groups.find((g) => g.key === active);

  const cities = useMemo(() => {
    const set = new Set();
    partners.forEach((p) => p.city && set.add(p.city));
    return Array.from(set).sort();
  }, [partners]);

  const filteredPartners = useMemo(() => {
    if (!activeGroup) return [];
    if (activeGroup.exclusiveFlag) {
      // Curated slot — partners flagged as `eventom_exclusive: true` by admin.
      return partners.filter((p) => p.eventom_exclusive && (!cityFilter || (p.city || "").toLowerCase() === cityFilter.toLowerCase()));
    }
    return partners.filter((p) => activeGroup.types.includes(p.type) && (!cityFilter || (p.city || "").toLowerCase() === cityFilter.toLowerCase()));
  }, [partners, activeGroup, cityFilter]);

  const showSponsors = active === "sponsors";
  const toggle = (id) => setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));

  return (
    <section id="directory" className="max-w-6xl mx-auto px-4 sm:px-6 py-14" data-testid="partner-directory">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
        <div>
          <div className="font-mono text-[10px] tracking-[0.3em] uppercase text-skyx mb-2">Biz Marketplace</div>
          <h2 className="font-display text-4xl ribbon-line">Grow Your <span className="marker-lime">Business</span></h2>
          <p className="text-sm text-muted mt-5 max-w-lg">10 curated marketplace categories. Pick verified artists, vendors, crews and brand sponsors — raise one combo enquiry and we package it for you.</p>
        </div>
        {groups.length > 0 && (
          <div className="flex flex-wrap gap-2" data-testid="dir-filters">
            {groups.map((g) => (
              <button key={g.key} onClick={() => setParams({ genre: g.key })} data-testid={`dir-filter-${g.key}`}
                className={`chip !text-[10px] ${active === g.key ? "!bg-lime !text-ink !border-lime" : ""}`}>{g.label}</button>
            ))}
          </div>
        )}
      </div>

      {!showSponsors && cities.length > 1 && (
        <div className="flex flex-wrap gap-2 items-center mb-5" data-testid="dir-city-filters">
          <span className="text-[10px] font-mono uppercase tracking-widest text-muted">City:</span>
          <button onClick={() => setCityFilter("")} data-testid="dir-city-all" className={`chip !text-[10px] ${!cityFilter ? "!bg-skyx !text-bg !border-skyx" : ""}`}>All cities</button>
          {cities.map((c) => (
            <button key={c} onClick={() => setCityFilter(c)} data-testid={`dir-city-${c.replace(/\s+/g, "-").toLowerCase()}`}
              className={`chip !text-[10px] ${cityFilter === c ? "!bg-skyx !text-bg !border-skyx" : ""}`}>{c}</button>
          ))}
        </div>
      )}

      {loading ? (
        <div className="glass p-6 font-mono text-sm text-muted">Loading directory…</div>
      ) : showSponsors ? (
        brandSponsors.length === 0 ? (
          <div className="glass p-8 text-center" data-testid="sponsors-empty">
            <div className="font-heading font-bold text-lg">No sponsors listed in your city yet.</div>
            <div className="text-sm text-muted mt-1">Brands actively working with Eventom will appear here — check back after Monday drops.</div>
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="sponsor-grid">
            {brandSponsors.map((b) => (
              <div key={b.id} className="glass p-5 card-lift" data-testid={`sponsor-card-${b.id}`}>
                <div className="flex items-center justify-between gap-2">
                  <span className="chip !text-[9px] !text-purple !border-purple/40">Sponsor</span>
                  <span className="inline-flex items-center gap-1 text-[9px] font-mono uppercase tracking-widest text-skyx"><BadgeCheck className="w-3.5 h-3.5" /> Verified</span>
                </div>
                <div className="flex items-start gap-3 mt-3">
                  {b.logo_url ? (
                    <img src={b.logo_url} alt={b.brand_name} loading="lazy" className="w-16 h-16 rounded-2xl object-cover border border-white/12 shrink-0" data-testid={`sponsor-logo-${b.id}`} />
                  ) : (
                    <div className="w-16 h-16 rounded-2xl bg-white/5 border border-white/10 flex items-center justify-center font-display text-2xl shrink-0">{(b.brand_name || "?")[0]}</div>
                  )}
                  <div className="min-w-0">
                    <div className="font-display text-2xl leading-tight truncate">{b.brand_name}</div>
                    <div className="text-[11px] font-mono uppercase tracking-widest text-muted mt-0.5">{b.industry}</div>
                    {b.target_cities && <div className="text-[11px] font-mono text-skyx mt-1 truncate"><MapPin className="inline w-3 h-3 mr-1" />{b.target_cities}</div>}
                  </div>
                </div>
                <div className="mt-4 flex items-center gap-2 flex-wrap">
                  <a href={`mailto:eventomlive@gmail.com?subject=${encodeURIComponent(`Sponsorship proposal for ${b.brand_name}`)}&body=${encodeURIComponent(`Hi Eventom, I'd like to submit a sponsorship proposal for ${b.brand_name}.`)}`}
                    data-testid={`sponsor-mail-${b.id}`} className="btn-lime !py-2 !px-3 !text-[10px]">
                    <MessageCircle className="w-3.5 h-3.5" /> Mail your proposal
                  </a>
                  {b.website && (
                    <a href={b.website.startsWith("http") ? b.website : `https://${b.website}`} target="_blank" rel="noreferrer noopener"
                      data-testid={`sponsor-web-${b.id}`} className="inline-flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-widest text-skyx hover:text-white transition-colors">
                      Website <ExternalLink className="w-3 h-3" />
                    </a>
                  )}
                </div>
              </div>
            ))}
          </div>
        )
      ) : filteredPartners.length === 0 ? (
        <div className="glass p-8 text-center" data-testid="directory-empty">
          <span className="tile w-14 h-14 mx-auto mb-4"><Users className="w-6 h-6 relative z-10" /></span>
          <div className="font-heading font-bold text-lg">No partners in this category{cityFilter ? ` for ${cityFilter}` : ""} yet.</div>
          <div className="text-sm text-muted mt-1">Try another filter, or submit a listing above to claim a spot.</div>
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {filteredPartners.map((p, idx) => {
            const picked = selected.includes(p.id);
            return (
              <div key={p.id} className={`glass p-5 card-lift ${picked ? "!border-lime" : ""}`} data-testid={`partner-card-${p.id}`}>
                <div className="flex items-center justify-between gap-2">
                  <span className={`chip !text-[9px] ${idx % 3 === 0 ? "!text-skyx !border-skyx/40" : idx % 3 === 1 ? "!text-pink !border-pink/40" : "!text-lime !border-lime/40"}`}>{TYPE_LABEL[p.type] || p.type}</span>
                  <span className="inline-flex items-center gap-1 text-[9px] font-mono uppercase tracking-widest text-skyx"><BadgeCheck className="w-3.5 h-3.5" /> Verified</span>
                </div>
                <div className="flex items-start gap-3 mt-3">
                  {p.logo_url ? (
                    <img src={p.logo_url} alt={p.name} loading="lazy" data-testid={`partner-logo-${p.id}`}
                      className="w-16 h-16 rounded-2xl object-cover border border-white/12 shrink-0" />
                  ) : (
                    <div className="w-16 h-16 rounded-2xl bg-white/5 border border-white/10 flex items-center justify-center font-display text-2xl shrink-0">{(p.name || "?")[0]}</div>
                  )}
                  <div className="min-w-0">
                    <div className="font-display text-2xl leading-tight truncate">{p.name}</div>
                    <div className="text-[11px] font-mono uppercase tracking-widest text-muted mt-0.5"><MapPin className="inline w-3 h-3 mr-1 text-magenta" />{p.city}</div>
                    {p.followers && ["artist", "creator"].includes(p.type) && (
                      <span className="chip !text-[9px] !text-pink !border-pink/40 mt-1.5" data-testid={`partner-followers-${p.id}`}>
                        <Instagram className="w-3 h-3" /> {p.followers} followers
                      </span>
                    )}
                  </div>
                </div>
                <div className="text-sm text-text/80 mt-2 line-clamp-3">{p.services}</div>
                <MediaSlider links={p.media_links || []} testId={`partner-media-${p.id}`} />
                <div className="mt-4 flex items-center gap-2 flex-wrap">
                  <button onClick={() => toggle(p.id)} data-testid={`partner-select-${p.id}`}
                    className={picked ? "btn-lime !py-2 !px-3 !text-[10px]" : "btn-ghost !py-2 !px-3 !text-[10px]"}>
                    {picked ? <><Check className="w-3.5 h-3.5" /> Added</> : <><PackagePlus className="w-3.5 h-3.5" /> Add to combo</>}
                  </button>
                  <a href={`https://wa.me/${EVENTOM_WA}?text=${encodeURIComponent(`Hi Eventom! I'd like to book ${p.name} (${TYPE_LABEL[p.type] || p.type}, ${p.city}) for my event. Please share availability and pricing.`)}`}
                    target="_blank" rel="noreferrer noopener" data-testid={`partner-book-${p.id}`}
                    className="btn-purple !py-2 !px-3 !text-[10px]">
                    <MessageCircle className="w-3.5 h-3.5" /> Book
                  </a>
                  {p.portfolio_or_instagram && (
                    <a href={p.portfolio_or_instagram.startsWith("http") ? p.portfolio_or_instagram : `https://instagram.com/${p.portfolio_or_instagram.replace(/^@/, "")}`}
                      target="_blank" rel="noreferrer noopener" data-testid={`partner-portfolio-${p.id}`}
                      className="inline-flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-widest text-skyx hover:text-white transition-colors">
                      Portfolio <ExternalLink className="w-3 h-3" />
                    </a>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
      <ComboBar selected={selected} partners={partners} clear={() => setSelected([])} />
    </section>
  );
}

export default function EcosystemPortal() {
  const [form, setForm] = useState({ name: "", type: "artist", city: "", services: "", portfolio_or_instagram: "", contact_mobile: "", logo_url: "", followers: "" });
  const [media, setMedia] = useState(["", "", ""]);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [gate, setGate] = useState(false);
  const formRef = useRef(null);

  const pickLogo = (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    if (f.size > 400 * 1024) return toast.error("Image must be under 400KB");
    const r = new FileReader();
    r.onload = () => setForm((s) => ({ ...s, logo_url: r.result }));
    r.readAsDataURL(f);
  };

  const submit = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      await api.post("/ecosystem/leads", { ...form, media_links: media.map((m) => m.trim()).filter(Boolean) });
      setDone(true); setGate(true); toast.success("Submitted for review");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Failed"); }
    finally { setBusy(false); }
  };

  const jumpToForm = (typeKey) => {
    setForm((s) => ({ ...s, type: typeKey }));
    setTimeout(() => formRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 50);
  };

  return (
    <Layout>
      <SocialGate open={gate} onClose={() => setGate(false)} title="Listing submitted. Now get booked."
        sub="Gigs, brand briefs and last-minute calls drop in our WhatsApp and Instagram communities first." />
      <div className="max-w-6xl mx-auto px-4 sm:px-6 pt-6"><GenieCard context="ecosystem" sub="Find gigs, brands & collaborations" testId="genie-card-ecosystem" /></div>

      {/* -------- HERO -------- */}
      <section className="relative overflow-hidden poster-canvas border-b border-white/5" data-testid="ecosystem-hero">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-14 md:py-20 grid lg:grid-cols-2 gap-10 items-center">
          <div className="relative z-10">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-lime/40 mb-4">
              <span className="w-1.5 h-1.5 bg-lime rounded-full animate-pulse" />
              <span className="text-[10px] font-mono tracking-[0.3em] uppercase text-lime">Grow Your Business</span>
            </div>
            <h1 className="font-fancy uppercase text-white leading-[0.88]" style={{ fontSize: "clamp(44px, 7vw, 84px)" }}>
              Artists. Vendors.<br/>
              <span className="marker-lime">Get booked.</span>
            </h1>
            <div className="mt-6 h-[3px] w-40 bg-lime/50 rounded-full" />
            <p className="mt-6 text-white/75 text-base max-w-md leading-relaxed">
              List once. Every organiser and brand on Eventom discovers you. Free to list, human-reviewed, paid gigs drop in our WhatsApp first.
            </p>
            <div className="mt-8 flex flex-wrap items-center gap-4">
              <a href="#list" className="btn-poster" data-testid="eco-hero-list-cta">List your business →</a>
              <a href="#directory" className="text-white/85 text-sm font-heading font-bold underline underline-offset-4 hover:text-lime">Browse marketplace →</a>
            </div>
            <div className="mt-6 flex flex-wrap gap-2 text-[10px] font-mono uppercase tracking-widest">
              <span className="chip !text-[10px] !text-skyx !border-skyx/40">Sponsors</span>
              <span className="chip !text-[10px] !text-magenta !border-magenta/40">Artists</span>
              <span className="chip !text-[10px] !text-lime !border-lime/40">Vendors</span>
              <span className="chip !text-[10px] !text-orangex !border-orangex/40">Freelancers</span>
            </div>
            <div className="mt-8 signature-script text-2xl md:text-3xl">More than events.</div>
          </div>
          <div className="relative rounded-3xl overflow-hidden aspect-[4/5]">
            <img src="/assets/girl-street.webp" alt="Eventom creator community" className="w-full h-full object-cover" data-testid="eco-hero-photo" />
            <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
            <div className="absolute bottom-5 left-5 text-white">
              <div className="font-mono text-[10px] uppercase tracking-widest text-lime">Verified creators</div>
              <div className="font-fancy text-3xl md:text-4xl leading-none mt-1">All of India.</div>
            </div>
          </div>
        </div>
      </section>

      {/* THEMED YELLOW SIGNATURE STRIP — PDF magazine thread */}
      <section className="themed-yellow sticker-wall py-6 overflow-hidden" data-testid="eco-themed-strip">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 relative z-10 flex flex-wrap items-center justify-between gap-3">
          <div className="min-w-0">
            <div className="text-[10px] font-mono uppercase tracking-[0.3em] text-black/80 font-bold">● Open calls</div>
            <div className="font-fancy uppercase text-black leading-none mt-1" style={{ fontSize: "clamp(24px, 4vw, 44px)", textShadow: "2px 2px 0 rgba(255,255,255,0.4)" }}>
              Your Next <span className="text-striped" style={{ color: "#0A0A0F" }}>Gig.</span>
            </div>
          </div>
          <a href="#list" className="btn-pill-dark !text-[11px] !py-3 !px-5" data-testid="eco-themed-cta">List for free →</a>
        </div>
      </section>

      {/* -------- CATEGORY TILES — tap a tile to prefill the listing form -------- */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 -mt-6 mb-8" data-testid="eco-category-tiles">
        <div className="flex items-end justify-between gap-3 mb-4">
          <div>
            <div className="text-[10px] font-mono uppercase tracking-widest text-lime">Who's listing on Eventom</div>
            <div className="font-heading font-bold text-lg text-white">Pick your craft, list in 60 seconds.</div>
          </div>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-7 gap-2 sm:gap-3">
          {CATEGORIES.map((c) => {
            const Icon = c.icon;
            const styles = ACCENT_STYLES[c.accent] || ACCENT_STYLES.lime;
            return (
              <button
                key={c.key}
                type="button"
                onClick={() => jumpToForm(c.key)}
                data-testid={`eco-tile-${c.key}`}
                className={`group relative rounded-2xl border border-white/10 bg-white/[0.03] p-3 text-left transition-all active:scale-[0.97] ${styles.tileHover}`}
              >
                <span className={`inline-flex items-center justify-center w-9 h-9 rounded-xl border mb-2 ${styles.iconBg}`}>
                  <Icon className="w-4 h-4" />
                </span>
                <div className="font-heading font-bold text-[11px] text-white leading-tight">{c.label.split(" / ")[0].split(" (")[0]}</div>
                <div className="text-[9px] font-mono uppercase tracking-widest text-muted mt-0.5 group-hover:text-white/70">List →</div>
              </button>
            );
          })}
        </div>
      </section>

      {/* -------- LISTING FORM -------- */}
      <section id="list" className="max-w-2xl mx-auto px-4 sm:px-6 py-8" ref={formRef}>
        {done ? (
          <div className="glass p-8 text-center" data-testid="ecosystem-lead-success">
            <span className="tile tile-lime w-14 h-14 mx-auto mb-4"><Rocket className="w-6 h-6 relative z-10" /></span>
            <div className="font-display text-3xl">Submitted for review.</div>
            <div className="text-muted mt-2">Our team reviews listings within 48 hours. Once approved, your card and showreel go live in the directory below.</div>
          </div>
        ) : (
          <form onSubmit={submit} className="glass p-6 space-y-3 relative overflow-hidden" data-testid="ecosystem-lead-form">
            <div className="pointer-events-none absolute -right-8 -top-8 w-40 h-40 rounded-full bg-lime/10 blur-3xl" />
            <div className="pointer-events-none absolute -left-10 -bottom-10 w-40 h-40 rounded-full bg-magenta/10 blur-3xl" />
            <div className="relative">
              <div className="font-display text-3xl">List your business</div>
              <div className="text-xs text-muted mb-1">Free to list on Biz with Eventom. Every submission is human-reviewed within 48 hours and appears on the public marketplace + B2B page.</div>
            </div>
            <input required data-testid="eco-name" placeholder="Your name / stage name / company" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="input-dark" />

            {/* Category — visual icon picker (replaces flat dropdown) */}
            <div>
              <label className="text-[10px] font-mono uppercase tracking-widest text-muted">Category</label>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-1.5 mt-1.5 max-h-52 overflow-y-auto pr-1" data-testid="eco-type-picker">
                {CATEGORIES.map((c) => {
                  const Icon = c.icon;
                  const active = form.type === c.key;
                  const styles = ACCENT_STYLES[c.accent] || ACCENT_STYLES.lime;
                  return (
                    <button
                      key={c.key}
                      type="button"
                      onClick={() => setForm({ ...form, type: c.key })}
                      data-testid={`eco-type-${c.key}`}
                      className={`flex items-center gap-2 rounded-xl border px-2.5 py-2 text-[11px] font-heading font-bold text-left transition-all ${active ? `${styles.activeBorder} text-white` : "border-white/10 bg-white/[0.03] text-white/85 hover:border-white/25"}`}
                    >
                      <Icon className={`w-3.5 h-3.5 shrink-0 ${active ? styles.activeIcon : "text-muted"}`} />
                      <span className="truncate">{c.label.split(" / ")[0].split(" (")[0]}</span>
                    </button>
                  );
                })}
              </div>
              {/* Hidden select kept for a11y / form-submit compatibility */}
              <select
                value={form.type}
                onChange={(e) => setForm({ ...form, type: e.target.value })}
                data-testid="eco-type"
                className="sr-only"
                aria-label="Category"
              >
                {TYPES.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
              </select>
            </div>

            <input required data-testid="eco-city" placeholder="City" value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} className="input-dark" />
            <div className="glass !rounded-2xl p-4 flex items-center gap-4">
              <div className="w-16 h-16 rounded-xl bg-black/40 border border-white/10 flex items-center justify-center overflow-hidden shrink-0">
                {form.logo_url ? <img src={form.logo_url} alt="" className="w-full h-full object-cover" data-testid="eco-logo-preview" /> : <ImagePlus className="w-5 h-5 text-muted" />}
              </div>
              <div className="min-w-0">
                <label className="btn-ghost !py-2 !px-3 !text-[11px] cursor-pointer" data-testid="eco-logo-upload">
                  Upload logo / profile pic
                  <input type="file" accept="image/*" className="hidden" onChange={pickLogo} data-testid="eco-logo-input" />
                </label>
                <div className="text-[10px] text-muted mt-1">Square <span className="text-white">800×800 px</span> · under 400KB · this is your main directory picture.</div>
              </div>
            </div>
            {["artist", "dj", "creator"].includes(form.type) && (
              <input data-testid="eco-followers" placeholder="Instagram followers (e.g. 45K) — we verify before approving" value={form.followers} onChange={(e) => setForm({ ...form, followers: e.target.value })} className="input-dark" />
            )}
            <textarea required data-testid="eco-services" placeholder={
              form.type === "dj" ? "e.g. 2-hour EDM/Bollywood mix, sound + lights kit included, retainer + travel"
              : form.type === "artist" ? "e.g. 45-min live set, 4-piece band, corporate + college friendly"
              : form.type === "photographer" ? "e.g. Wedding + event photography, 2 shooters, edited album in 10 days"
              : form.type === "videographer" ? "e.g. Reel + long-form aftermovies, drone shots, 4K delivery"
              : form.type === "production" ? "e.g. Stage rigging, backline, crew coordination for concerts"
              : form.type === "avsound" ? "e.g. Line array PA, mixing desk, lighting rig — up to 5000 pax"
              : form.type === "decor" ? "e.g. Themed set design, florals, LED walls, backdrop fabrication"
              : form.type === "venue" ? "e.g. 500-pax convention hall + green room + parking + AC"
              : form.type === "marketing" ? "e.g. Full-funnel event marketing — social + ATL + influencer"
              : form.type === "agency" ? "e.g. Talent booking + turnkey event management, PAN-India"
              : form.type === "vendor" ? "e.g. 500-pax vegetarian catering, mehendi artists, security bandobast"
              : form.type === "freelancer" ? "e.g. Emcee, stage manager, curator — role + languages spoken"
              : form.type === "sponsor" ? "e.g. Categories, target city+age, per-event budget range"
              : "What you offer, packages, and what makes you stand out"
            } value={form.services} onChange={(e) => setForm({ ...form, services: e.target.value })} className="input-dark resize-none" rows={2} />
            <div>
              <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-skyx mb-2">Showreel — photo / video links</div>
              {media.map((m, i) => (
                <input key={i} data-testid={`eco-media-${i}`} placeholder={i === 0 ? "Image URL or YouTube / Instagram link" : "Another link (optional)"}
                  value={m} onChange={(e) => setMedia(media.map((v, k) => (k === i ? e.target.value : v)))} className="input-dark mb-2 !text-xs" />
              ))}
              <div className="text-[10px] text-muted">These appear as a slider on your directory card.</div>
            </div>
            <input data-testid="eco-portfolio" placeholder="Portfolio URL or @instagram" value={form.portfolio_or_instagram} onChange={(e) => setForm({ ...form, portfolio_or_instagram: e.target.value })} className="input-dark" />
            <input required data-testid="eco-mobile" placeholder="Contact mobile (kept private)" value={form.contact_mobile} onChange={(e) => setForm({ ...form, contact_mobile: e.target.value })} className="input-dark" />
            <button disabled={busy} className="btn-primary w-full justify-center" data-testid="eco-submit">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Users className="w-4 h-4" />} Submit for Review
            </button>
          </form>
        )}
      </section>

      {/* -------- BIZ SHOP TILE — link into merch store (Iter 39 brief) -------- */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 mb-10" data-testid="biz-shop-tile">
        <Link to="/store" className="group relative block overflow-hidden rounded-3xl border border-magenta/40 bg-gradient-to-br from-magenta/20 via-pink/10 to-orangex/15 p-6 sm:p-8">
          <span className="pointer-events-none absolute -right-10 -top-10 w-52 h-52 rounded-full bg-magenta/25 blur-3xl" />
          <span className="pointer-events-none absolute -left-10 -bottom-10 w-52 h-52 rounded-full bg-orangex/20 blur-3xl" />
          <div className="relative flex flex-col sm:flex-row items-start sm:items-center gap-4 sm:gap-8">
            <span className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-magenta text-white shrink-0"><Package className="w-6 h-6" /></span>
            <div className="flex-1 min-w-0">
              <div className="chip !text-[10px] !text-magenta !border-magenta/50 mb-2">Shop</div>
              <div className="font-display text-3xl sm:text-4xl uppercase leading-none text-white">Wear the vibe.</div>
              <div className="text-sm text-white/80 mt-2">Merch drops, affiliate picks, event capsules.</div>
            </div>
            <span className="btn-lime !text-xs shrink-0 group-hover:scale-105 transition-transform">Shop now →</span>
          </div>
        </Link>
      </section>

      <PartnerDirectory />
    </Layout>
  );
}
